import React, { useState, useRef, useEffect, useCallback } from 'react';

const MAX_RECORDING_SECONDS = 60; // 60s max voice note

const AudioRecorder = ({ onRecordComplete, onCancel }) => {
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioBlob, setAudioBlob] = useState(null);
  const [isPlayingPreview, setIsPlayingPreview] = useState(false);
  const [permissionError, setPermissionError] = useState(null);

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const previewAudioRef = useRef(null);
  const streamRef = useRef(null);

  const stopTracks = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  }, []);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setRecording(false);
    stopTracks();
  }, [stopTracks]);

  const startRecording = useCallback(async () => {
    setPermissionError(null);
    chunksRef.current = [];

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1, // Mono channel avoids stereo sample clock mismatch
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      streamRef.current = stream;

      // Determine supported mimeType (prioritize native MP4 on Safari/WebKit to avoid 2x speed WebM bug)
      const isSafari = typeof navigator !== 'undefined' && /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      const mimeType = isSafari && MediaRecorder.isTypeSupported('audio/mp4')
        ? 'audio/mp4'
        : MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm')
        ? 'audio/webm'
        : MediaRecorder.isTypeSupported('audio/mp4')
        ? 'audio/mp4'
        : '';

      const mediaRecorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = () => {
        const type = mimeType || 'audio/webm';
        const blob = new Blob(chunksRef.current, { type });
        const url = URL.createObjectURL(blob);
        setAudioBlob(blob);
        setAudioUrl(url);
      };

      // Start recording without timeslice. Passing a timeslice (e.g. 100ms) fragments WebM clusters
      // and corrupts timecode scale in Chromium/WebKit on macOS, leading to 2x speed playback.
      mediaRecorder.start();
      setRecording(true);
      setElapsed(0);

      timerRef.current = setInterval(() => {
        setElapsed((prev) => {
          if (prev >= MAX_RECORDING_SECONDS - 1) {
            stopRecording();
            return MAX_RECORDING_SECONDS;
          }
          return prev + 1;
        });
      }, 1000);
    } catch (err) {
      console.error('Microphone access error:', err);
      setPermissionError('Microphone permission denied or not supported.');
    }
  }, [stopRecording]);

  useEffect(() => {
    startRecording();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      stopTracks();
    };
  }, [startRecording, stopTracks]);

  const handleCancel = () => {
    stopRecording();
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    if (onCancel) onCancel();
  };

  const handleConfirm = () => {
    if (!audioBlob) return;
    const isMp4 = audioBlob.type.includes('mp4') || audioBlob.type.includes('aac') || audioBlob.type.includes('m4a');
    const extension = isMp4 ? 'mp4' : 'webm';
    const file = new File([audioBlob], `voice_bazz_${Date.now()}.${extension}`, {
      type: audioBlob.type,
    });
    if (onRecordComplete) {
      onRecordComplete({ file, blob: audioBlob, url: audioUrl, duration: elapsed });
    }
  };

  const togglePreviewPlay = () => {
    if (!previewAudioRef.current) return;
    if (isPlayingPreview) {
      previewAudioRef.current.pause();
    } else {
      previewAudioRef.current.play().catch(console.warn);
    }
  };

  const formatTime = (secs) => {
    const m = Math.floor(secs / 60);
    const s = Math.floor(secs % 60);
    return `${m}:${s < 10 ? '0' : ''}${s}`;
  };

  if (permissionError) {
    return (
      <div className="p-3 my-2 rounded-2xl bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 text-red-600 dark:text-red-400 text-xs flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <i className="fas fa-microphone-slash text-base" />
          <span>{permissionError}</span>
        </div>
        <button
          type="button"
          onClick={handleCancel}
          className="text-xs font-bold text-red-600 dark:text-red-400 hover:underline"
        >
          Dismiss
        </button>
      </div>
    );
  }

  return (
    <div className="my-2.5 p-3 rounded-2xl bg-zinc-100 dark:bg-zinc-800/80 border border-zinc-200 dark:border-zinc-700 flex items-center justify-between gap-3 shadow-xs">
      {recording ? (
        <>
          <div className="flex items-center gap-3">
            <span className="w-3 h-3 rounded-full bg-red-500 animate-ping" />
            <span className="font-mono text-sm font-bold text-red-600 dark:text-red-400">
              {formatTime(elapsed)}
            </span>
            <div className="flex items-center gap-1 h-5">
              {[40, 80, 50, 100, 65, 90, 45, 75, 60, 95].map((h, i) => (
                <div
                  key={i}
                  className="w-1 bg-red-500/80 rounded-full animate-pulse"
                  style={{
                    height: `${h}%`,
                    animationDelay: `${(i * 120) % 600}ms`,
                  }}
                />
              ))}
            </div>
            <span className="text-xs text-zinc-500 dark:text-zinc-400 hidden sm:inline">
              Recording Voice Bazz...
            </span>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleCancel}
              className="w-8 h-8 rounded-full flex items-center justify-center text-zinc-400 hover:text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-950/40 transition-colors"
              title="Cancel recording"
            >
              <i className="fas fa-trash-alt text-xs" />
            </button>
            <button
              type="button"
              onClick={stopRecording}
              className="px-3 py-1.5 rounded-xl bg-red-500 hover:bg-red-600 text-white text-xs font-bold shadow-xs transition-colors flex items-center gap-1.5"
            >
              <i className="fas fa-stop text-[10px]" />
              Done
            </button>
          </div>
        </>
      ) : (
        <>
          <audio
            ref={previewAudioRef}
            src={audioUrl}
            onPlay={() => setIsPlayingPreview(true)}
            onPause={() => setIsPlayingPreview(false)}
            onEnded={() => setIsPlayingPreview(false)}
          />

          <div className="flex items-center gap-3 flex-1 min-w-0">
            <button
              type="button"
              onClick={togglePreviewPlay}
              className="w-8 h-8 rounded-full bg-sky-500 text-white flex items-center justify-center hover:bg-sky-600 transition-colors shrink-0"
            >
              <i className={`fas ${isPlayingPreview ? 'fa-pause' : 'fa-play ml-0.5'} text-xs`} />
            </button>
            <div className="flex flex-col min-w-0">
              <span className="text-xs font-semibold text-zinc-900 dark:text-zinc-100 truncate">
                Voice Note Recorded ({formatTime(elapsed)})
              </span>
              <span className="text-[10px] text-zinc-500 dark:text-zinc-400">
                Click play to preview
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleCancel}
              className="w-8 h-8 rounded-full flex items-center justify-center text-zinc-400 hover:text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-950/40 transition-colors"
              title="Delete audio"
            >
              <i className="fas fa-trash-alt text-xs" />
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              className="px-3.5 py-1.5 rounded-xl bg-sky-500 hover:bg-sky-600 text-white text-xs font-bold shadow-xs transition-colors flex items-center gap-1"
            >
              <i className="fas fa-check text-[10px]" />
              Attach
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default AudioRecorder;
